import { inject, Injectable } from '@angular/core';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { getQuoteToolStorageKey } from '@lx/quote-tool/lite/shared/types';
import { TripDatesService } from '@lx/quote-tool/shared/services';
import { StudentDetails, TuitionDates } from '@lx/quote-tool/shared/types';
import { Course } from '@lx/shared/data-access/types';
import { LoggerService } from '@lx/shared/logger';
import { SessionStorageService } from '@lx/shared/storage';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, Observable, of, switchMap, tap } from 'rxjs';
import { StepsDefinitionService } from '../services';
import { OnlineCourseDetails, RouteIds, Step, STORE_RESET_KEY } from '../types';
import { OnlineQuoteToolState } from './online-quote-tool.state';

/**
 * State Store using ComponentStore for reactive state management
 * Following quote-tool-lite pattern with ComponentStore
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineQuoteToolStore extends ComponentStore<OnlineQuoteToolState> {
  private readonly stepsDefinitionService = inject(StepsDefinitionService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly tripDatesService = inject(TripDatesService);
  private readonly storageKey = getQuoteToolStorageKey();
  private readonly loggerService = inject(LoggerService);

  // Selectors - Navigation
  readonly currentStep$ = this.select(({ currentStep }) => currentStep);
  readonly currentStepId$ = this.select(({ currentStep }) => currentStep?.id);

  // Selectors - Country & Currency
  readonly currency$ = this.select(({ currency }) => currency);

  // Selector for phone code
  readonly phoneCode$ = this.select(({ phoneCode }) => phoneCode);

  // Selectors - Course Selection
  readonly selectedCourse$ = this.select(({ selectedCourse }) => selectedCourse);
  readonly selectedDuration$ = this.select(({ selectedDuration }) => selectedDuration);
  readonly selectedStartDate$ = this.select(({ selectedStartDate }) => selectedStartDate);
  readonly selectedEndDate$ = this.select(({ selectedEndDate }) => selectedEndDate);
  readonly totalPrice$ = this.select(({ grandTotal }) => grandTotal?.[0]);

  /**
   * Combined selector for course summary data
   * Returns all data needed by CourseSummaryComponent in a single emission
   */
  readonly courseSummary$ = this.select(
    ({ selectedCourse, selectedDuration, selectedStartDate, selectedEndDate, grandTotal, currency }) => ({
      courseDetails: selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      grandTotal,
      currency,
    }),
    {
      debounce: true,
      equal: (prev, curr) =>
        prev.courseDetails?.code === curr.courseDetails?.code &&
        prev.selectedDuration === curr.selectedDuration &&
        prev.selectedStartDate === curr.selectedStartDate &&
        prev.selectedEndDate === curr.selectedEndDate &&
        prev.grandTotal?.[0]?.value === curr.grandTotal?.[0]?.value &&
        prev.currency === curr.currency,
    },
  );

  readonly studentDetails$ = this.select(({ studentDetails }) => studentDetails);
  readonly studentFirstName$ = this.select(({ studentDetails }) => studentDetails?.firstName ?? '');

  // Combined selector for price calculation trigger
  readonly selectionForPriceCalculation$ = this.selectCourseSelectionData();

  // Combined selector for trip dates calculation trigger
  readonly selectionForTripDatesCalculation$ = this.selectCourseSelectionData();

  readonly selectionForLeads$ = this.select(
    ({ residenceCountryCode, selectedSchool, selectedCourse, selectedDuration, selectedStartDate, selectedEndDate, currency, studentDetails, quoteId }) => ({
      residenceCountryCode,
      selectedSchool,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      currency,
      studentDetails,
      quoteId,
    }),
    { debounce: true },
  );

  /**
   * Combined selector for Google Analytics dataLayer base quote data.
   * Includes all fundamental quote information needed for analytics tracking.
   */
  readonly baseSelectionForDataLayer$ = this.select(
    ({ requestedQuotes, selectedCourse, selectedDuration, selectedStartDate, selectedEndDate }) => ({
      quote: requestedQuotes ?? 1,
      programType: 'online',
      course: selectedCourse?.name,
      duration: selectedDuration,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
    }),
    { debounce: true },
  );

  /**
   * Combined selector for deeplink generation and validation
   * Returns only the data needed to generate shareable deeplinks
   */
  readonly deeplinkSelectionData$ = this.select(
    ({ residenceCountryCode, selectedSchool, selectedCourse, selectedDuration, selectedStartDate }) => ({
      residenceCountryCode,
      selectedSchool,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
    }),
    {
      debounce: true,
      equal: (prev, curr) =>
        prev.residenceCountryCode === curr.residenceCountryCode &&
        prev.selectedSchool === curr.selectedSchool &&
        prev.selectedCourse?.code === curr.selectedCourse?.code &&
        prev.selectedDuration === curr.selectedDuration &&
        prev.selectedStartDate === curr.selectedStartDate,
    },
  );

  // Updaters - Navigation
  readonly updateCurrentStep = this.updater((state, currentStep: Step) => ({
    ...state,
    currentStep,
  }));

  // Updaters - Residence data
  readonly updateResidenceData = this.updater(
    (
      state,
      {
        residenceCountryCode,
        phoneCode,
        currency,
      }: {
        residenceCountryCode: string;
        phoneCode?: string;
        currency: string;
      },
    ) => ({
      ...state,
      residenceCountryCode,
      phoneCode,
      currency,
    }),
  );

  // Updaters - School
  readonly updateSelectedSchool = this.updater((state, selectedSchool: string) => ({
    ...state,
    selectedSchool,
  }));

  readonly updateState = this.updater((state, partialState: Partial<OnlineQuoteToolState>) => ({
    ...state,
    ...partialState,
  }));

  /**
   * Resets the store to its default state while preserving user's country and currency selection.
   * Those values are set when app is initialised, user can't select them on any way, so
   * there is no reason to have to take them again after resetting the store.
   */
  readonly resetStore = this.updater((state) => {
    const { residenceCountryCode, currency, selectedSchool } = state;

    return {
      ...this.getDefaultState(),
      residenceCountryCode,
      currency,
      selectedSchool,
    };
  });

  // Updaters - Course Selection
  readonly updateSelectedCourse = this.updater((state, course: Course) => {
    return {
      ...state,
      selectedCourse: this.transformCourseToDetails(course),
    };
  });

  readonly updateSelectedDuration = this.updater((state, selectedDuration: number) => ({
    ...state,
    selectedDuration,
  }));

  readonly updateSelectedStartDate = this.updater((state, selectedStartDate: string) => ({
    ...state,
    selectedStartDate,
  }));

  /**
   * Updates the grand total together with the currency it is expressed in.
   * The quote response can be in a different currency than the residence one (e.g. single currency mode),
   * so the currency is kept in sync with the prices to guarantee they can be displayed.
   */
  readonly updatePricing = this.updater((state, { grandTotal, currency }: { grandTotal: LimitedCurrencyAmount[]; currency?: string }) => ({
    ...state,
    grandTotal,
    currency: currency ?? state.currency,
  }));

  /**
   * Effect to calculate trip dates when selection changes.
   * Responds reactively to changes in school, course, duration, or start date.
   *
   * Called from initializeStore() for discoverability
   * Single source of truth for trip dates logic
   */
  readonly updateTripDates$ = this.effect(
    (
      trigger$: Observable<{
        selectedSchool: string;
        selectedCourse: OnlineCourseDetails;
        selectedDuration: number;
        selectedStartDate: string;
      }>,
    ) => {
      return trigger$.pipe(
        switchMap(({ selectedSchool, selectedCourse, selectedDuration, selectedStartDate }) => {
          // Validate all required fields are present
          if (!selectedSchool || !selectedCourse?.code || !selectedDuration || !selectedStartDate) {
            return of(null);
          }

          return this.tripDatesService.getTuitionDates(selectedSchool, selectedCourse.code, selectedDuration, selectedStartDate).pipe(
            catchError((error) => {
              this.loggerService.error('Failed to calculate trip dates:', error);
              return EMPTY;
            }),
          );
        }),
        tap((dates: TuitionDates | null) => {
          if (dates?.endDate) {
            this.patchState({ selectedEndDate: dates.endDate });
          }
        }),
      );
    },
  );

  readonly updateStudentInformation = this.updater((state, studentInformation: Partial<StudentDetails>) => ({
    ...state,
    studentDetails: {
      ...state.studentDetails,
      ...studentInformation,
    },
  }));

  constructor() {
    super({});
  }

  /**
   * Initializes the store with the state from the session storage if it exists.
   * If not, it will initialize the store with the default state.
   *
   * Before restoring state, checks for a cleanup flag (STORE_RESET_KEY).
   * The flag is set when the user explicitly navigates away from the app
   * (e.g., clicked "Back to Portal" or closes the quote tool).
   *
   * When the flag is set, the store is reset to a fresh state to ensure data consistency.
   * This allows users to refresh pages without losing data, while ensuring a clean start
   * when they return after explicitly leaving the application.
   *
   * Also initializes reactive effects that should run for the lifetime of the store.
   */
  initializeStore(): void {
    // Check if reset is required (user explicitly navigated away)
    const resetRequired = this.sessionStorageService.get(STORE_RESET_KEY);

    if (resetRequired) {
      // Reset to fresh state - clear the flag and persisted state first
      this.sessionStorageService.delete(STORE_RESET_KEY);
      this.sessionStorageService.delete(this.storageKey);
      this.updateState({ ...this.getDefaultState() });
    } else {
      // Normal initialization - restore state if available
      const persistedData = this.sessionStorageService.get(this.storageKey) as Partial<
        OnlineQuoteToolState & {
          currentStepId: RouteIds;
        }
      > | null;
      const currentStepId = persistedData?.currentStepId;

      // Validate currentStepId is a valid RouteIds value - restore state if available
      if (persistedData && Object.keys(persistedData)?.length && currentStepId && Object.values(RouteIds).includes(currentStepId as RouteIds)) {
        const {
          residenceCountryCode,
          currency,
          selectedSchool,
          selectedCourse,
          selectedDuration,
          selectedStartDate,
          selectedEndDate,
          studentDetails,
          quoteId,
        } = persistedData || {};

        this.updateState({
          currentStep: this.stepsDefinitionService.getStepDefinition(currentStepId as RouteIds),
          residenceCountryCode,
          currency,
          selectedSchool,
          selectedCourse,
          selectedDuration,
          selectedStartDate,
          selectedEndDate,
          studentDetails,
          quoteId,
        });
      } else {
        this.updateState(this.getDefaultState());
      }
    }

    // Initialize reactive effects
    this.updateTripDates$(this.selectionForTripDatesCalculation$);
  }

  private getDefaultState(): OnlineQuoteToolState {
    return {
      currentStep: this.stepsDefinitionService.getStepDefinition(RouteIds.buildYourCourse),
    };
  }

  /**
   * Transforms Course domain model to CourseDetails state representation
   * @param course - The source Course object
   * @returns CourseDetails for state storage
   * @private
   */
  private transformCourseToDetails(course: Course): OnlineCourseDetails {
    const {
      type: { code: typeCode },
      code,
      name,
      shortName,
      benefits,
      durationMin,
      durationMax,
    } = course;

    return {
      type: typeCode,
      code,
      name,
      shortName,
      benefits,
      durationMin,
      durationMax,
    };
  }

  /**
   * Creates a selector for course selection data used by multiple calculations.
   * Extracts common selection logic for price and trip dates calculations.
   * @private
   */
  private selectCourseSelectionData() {
    return this.select(
      ({ selectedSchool, selectedCourse, selectedDuration, selectedStartDate }) => ({
        selectedSchool,
        selectedCourse,
        selectedDuration,
        selectedStartDate,
      }),
      {
        debounce: true,
        equal: (prev, curr) =>
          prev.selectedSchool === curr.selectedSchool &&
          prev.selectedCourse?.code === curr.selectedCourse?.code &&
          prev.selectedDuration === curr.selectedDuration &&
          prev.selectedStartDate === curr.selectedStartDate,
      },
    );
  }
}
